'use client';

import { useFeatureFlagVariantKey } from 'posthog-js/react';

/**
 * Viral CTA A/B Test Variant Configuration
 *
 * Story 6.11: Viral CTA A/B Testing
 *
 * PostHog Feature Flag: viral-cta-variant
 * 5 variants with equal 20% distribution:
 *   - control: "Create Your Own Boty - It's Free"
 *   - speed: "Make a Bot Like This in 2 Minutes"
 *   - curiosity: "How Was This Bot Made? Try It"
 *   - social: "Join 10,000+ Creators - Make Your Bot"
 *   - personal: "Want a Bot for Your Event?"
 */

export const FLAG_KEY = 'viral-cta-variant' as const;

export type ViralCTAVariant =
  | 'control'
  | 'speed'
  | 'curiosity'
  | 'social'
  | 'personal';

const VALID_VARIANTS: ViralCTAVariant[] = [
  'control',
  'speed',
  'curiosity',
  'social',
  'personal',
];

/**
 * i18n key map for each variant.
 * Keys resolve to: consumer.bot.viralCTA.{variant}
 */
export const VARIANT_I18N_KEYS: Record<ViralCTAVariant, string> = {
  control: 'viralCTA.control',
  speed: 'viralCTA.speed',
  curiosity: 'viralCTA.curiosity',
  social: 'viralCTA.social',
  personal: 'viralCTA.personal',
};

export interface UseViralCTAVariantReturn {
  variant: ViralCTAVariant;
  isLoading: boolean;
}

/**
 * Hook to get the viral CTA variant from PostHog feature flag.
 * Falls back to 'control' if PostHog is unavailable or loading.
 */
export function useViralCTAVariant(): UseViralCTAVariantReturn {
  const variantKey = useFeatureFlagVariantKey(FLAG_KEY);

  const isLoading = variantKey === undefined;
  const variant = validateVariant(variantKey);

  return { variant, isLoading };
}

function validateVariant(value: unknown): ViralCTAVariant {
  if (
    typeof value === 'string' &&
    VALID_VARIANTS.includes(value as ViralCTAVariant)
  ) {
    return value as ViralCTAVariant;
  }
  return 'control';
}
